import { addWarning } from "@wayflyer/blackbox-fe";
import type { FundingApplicationSchemas } from "api-types";
import { createContext, useContext } from "react";
import { useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { CONNECT_APP_ROUTE_PATHS, NAVIGATION_PATHS, ORDERED_CONNECTOR_SLUGS } from "../../components/constants";
import type { FormattedFundingApplicationScreen } from "../../queries/funding-application/queries";
import {
  isRejectedFundingApplicationStateTransition,
  useFundingApplicationStateMutation,
} from "../../queries/funding-application/queries";
import { UploadDocumentType } from "../../types";
import { COMMON_MESSAGES } from "../../components/common-messages";
import { isGoogleAnalyticsRequired } from "../../utils/funding-journey";

import { MESSAGES, BUTTON_MESSAGES } from "./message-definitions";

export enum PAGE_TYPE {
  CONNECTORS = "CONNECTORS",
  BANKING = "BANKING",
  BANK_STATEMENTS_UPLOAD = "BANK_STATEMENTS_UPLOAD",
  DOCUMENTS = "DOCUMENTS",
  CREDIT_CHECK_CONSENT = "CREDIT_CHECK_CONSENT",
}

const pagePaths = {
  [PAGE_TYPE.CONNECTORS]: NAVIGATION_PATHS.ADD_CONNECTION,
  [PAGE_TYPE.BANKING]: NAVIGATION_PATHS.BANKING,
  [PAGE_TYPE.BANK_STATEMENTS_UPLOAD]: NAVIGATION_PATHS.BANK_STATEMENTS_UPLOAD,
  [PAGE_TYPE.DOCUMENTS]: `/${CONNECT_APP_ROUTE_PATHS.UPLOADS}`,
  [PAGE_TYPE.CREDIT_CHECK_CONSENT]: NAVIGATION_PATHS.CREDIT_CHECK_CONSENT,
};

export const composePath = (pageType: PAGE_TYPE, pageStep?: string): string => {
  if (pageStep) {
    if (pageType === PAGE_TYPE.CONNECTORS) {
      return `${pagePaths[pageType]}?platform=${pageStep}`;
    } else if (pageType === PAGE_TYPE.DOCUMENTS) {
      return `${pagePaths[pageType]}?document=${pageStep}`;
    }
  }

  return pagePaths[pageType];
};

export interface StepContext {
  currentPage: PAGE_TYPE;
  currentStep?: string; // e.g. connector slug or document type
}

export interface FundingApplicationStepContext extends StepContext {
  previousPage?: PAGE_TYPE;
  previousStep?: string;

  nextPage?: PAGE_TYPE;
  nextStep?: string;

  connectorSlugs: string[];
  documents: string[];
}

export interface NavigationProps {
  nextPath?: string;
  previousPath?: string;
  nextPageType?: PAGE_TYPE;
  nextButtonLabel: string;
  backButtonLabel: string;
  navigateWithStateUpdate: (path: string) => Promise<void>;
  navigate: (path: string) => void;
  currentStepIndex: number;
  stepString: string;
}

interface ApplicationJourneyPage {
  page: PAGE_TYPE;
  steps?: string[];
}

/**
 * For the current page (and step) in the navigation flow, find the previous path
 * @param navigationFlow
 * @param currentPageIndex
 * @param currentStep
 * @returns
 */
export const findPreviousPath = (
  navigationFlow: ApplicationJourneyPage[],
  currentPageIndex: number,
  currentStep?: string
): string => {
  const currentPage = navigationFlow[currentPageIndex];

  // if the current page has steps and not at the first step, go back to the previous step
  if (currentStep && currentPage.steps && currentPage.steps.length) {
    const currentStepIndex = currentPage.steps.findIndex((step) => step === currentStep);

    if (currentStepIndex < 0) {
      // this means something has gone wrong so log a warning and fall through to the last step of the previous page
      addWarning(
        `Unknown step ${currentStep} passed to findPreviousPath for page ${currentPage.page}. Known steps are ${currentPage.steps}`
      );
    } else if (currentStepIndex !== 0) {
      return composePath(currentPage.page, currentPage.steps[currentStepIndex - 1]);
    }
  }

  if (currentPageIndex === 0) {
    return "";
  }

  const previousPage = navigationFlow[currentPageIndex - 1];
  // if previous page has steps, go to the last step of the previous page
  return composePath(
    previousPage.page,
    previousPage.steps && previousPage.steps.length ? previousPage.steps[previousPage.steps.length - 1] : ""
  );
};

export const findPreviousPageContext = (
  navigationFlow: ApplicationJourneyPage[],
  currentPageIndex: number,
  currentStep?: string
): { pageType?: PAGE_TYPE; pageStep?: string } => {
  if (currentPageIndex <= 0) {
    return {};
  }
  const currentPage = navigationFlow[currentPageIndex];
  const previousPage = navigationFlow[currentPageIndex - 1];

  if (currentStep && currentPage.steps && currentPage.steps.length) {
    const currentStepIndex = currentPage.steps.findIndex((step) => step === currentStep);
    if (currentStepIndex > 0) {
      return { pageType: currentPage.page, pageStep: currentPage.steps[currentStepIndex - 1] };
    }
  }

  return {
    pageType: previousPage.page,
    pageStep: previousPage.steps?.length ? previousPage.steps[previousPage.steps.length - 1] : undefined,
  };
};

/**
 * For the current page (and step) in the navigation flow, find the next path
 * @param navigationFlow
 * @param currentPageIndex
 * @param currentStep
 * @returns
 */
export const findNextPath = (
  navigationFlow: ApplicationJourneyPage[],
  currentPageIndex: number,
  currentStep?: string
): string => {
  const currentPage = navigationFlow[currentPageIndex];

  // if the current page has steps and not at the last step, go to the next step
  if (currentStep && currentPage.steps && currentPage.steps.length) {
    const currentStepIndex = currentPage.steps.findIndex((step) => step === currentStep);
    if (currentStepIndex < 0) {
      // this means something has gone wrong so log a warning and fall through to the first step of the current page
      addWarning(
        `Unknown step ${currentStep} passed to findNextPath for page ${currentPage.page}. Known steps are ${currentPage.steps}`
      );
      return composePath(currentPage.page, currentPage.steps[0]);
    }
    if (currentStepIndex !== currentPage.steps.length - 1) {
      return composePath(currentPage.page, currentPage.steps[currentStepIndex + 1]);
    }
  }

  if (currentPageIndex === navigationFlow.length - 1) {
    return "";
  }

  const nextPage = navigationFlow[currentPageIndex + 1];
  // if next page has steps, go to the first step of the next page
  return composePath(nextPage.page, nextPage.steps && nextPage.steps.length ? nextPage.steps[0] : "");
};

export const useFundingApplicationNavigation = ({
  currentPage,
  currentStep,
  startPagesCount = 2,
}: {
  currentPage: PAGE_TYPE;
  currentStep?: string;
  startPagesCount?: number;
}): NavigationProps => {
  const { navigate } = useSpaNavigate("FundingApp");
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const intl = useIntl();
  const applicationStateMutation = useFundingApplicationStateMutation(customerDomain);
  const navigationContext = useNavigationContext();

  //TODO: remove when doing MCA-3133, BE will determine post-login path at that point
  const navigateWithStateUpdate = (path: string) => {
    return applicationStateMutation
      .mutateAsync({ applicationState: "in_progress", applicationSubState: path })
      .then(() => navigate(path))
      .catch((error) => {
        if (isRejectedFundingApplicationStateTransition(error)) {
          // Hook's onError has already redirected to root.
          return;
        }
        throw error;
      });
  };

  const currentNavigationPageIndex = navigationContext.navigationFlow.findIndex((step) => {
    return step.page === currentPage.valueOf();
  });

  // it's possible to not be able to get the current index if the state has been edited so default to first page of the journey
  // which will reset the subState when the user continues
  if (currentNavigationPageIndex < 0) {
    addWarning("Unable to determine current page in application flow", { currentPage, navigationContext });
    // eslint-disable-next-line react-hooks/immutability -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    window.location.href = `/${customerDomain}/funding/application${composePath(
      navigationContext.navigationFlow[0].page
    )}`;
  }

  const previousPath = findPreviousPath(navigationContext.navigationFlow, currentNavigationPageIndex, currentStep);
  const nextPath = findNextPath(navigationContext.navigationFlow, currentNavigationPageIndex, currentStep);
  const nextPageType = navigationContext.navigationFlow[currentNavigationPageIndex + 1]?.page;
  const nextPageSteps = navigationContext.navigationFlow[currentNavigationPageIndex + 1]?.steps;

  const previousPageContext = findPreviousPageContext(
    navigationContext.navigationFlow,
    currentNavigationPageIndex,
    currentStep
  );

  // Determine if the next navigation target is a step within the current page
  const currentFlowPage = navigationContext.navigationFlow[currentNavigationPageIndex];
  const currentPageSteps = currentFlowPage?.steps;
  let nextStepWithinPage: string | undefined;
  if (currentStep && currentPageSteps?.length) {
    const stepIndex = currentPageSteps.findIndex((s) => s === currentStep);
    if (stepIndex >= 0 && stepIndex < currentPageSteps.length - 1) {
      nextStepWithinPage = currentPageSteps[stepIndex + 1];
    }
  }

  const getDocumentLabel = (documentType?: string): string => {
    if (!documentType) {
      return intl.formatMessage(BUTTON_MESSAGES.documentsButton);
    }

    const documentLabelMap: Record<string, string> = {
      [UploadDocumentType.SUSA]: intl.formatMessage(BUTTON_MESSAGES.documentUploadSusa),
      [UploadDocumentType.POOL_BANCARIO]: intl.formatMessage(BUTTON_MESSAGES.documentUploadPoolBancario),
      [UploadDocumentType.CASH_FLOW_FORECAST]: intl.formatMessage(BUTTON_MESSAGES.documentUploadRevenueForecast),
      [UploadDocumentType.COMPANY_DEBT_AGREEMENT]: intl.formatMessage(BUTTON_MESSAGES.documentUploadDebtAgreement),
      [UploadDocumentType.MANAGEMENT_ACCOUNTS]: intl.formatMessage(BUTTON_MESSAGES.documentUploadManagementAccounts),
      [UploadDocumentType.PROFIT_AND_LOSS]: intl.formatMessage(BUTTON_MESSAGES.documentUploadProfitAndLoss),
      [UploadDocumentType.BALANCE_SHEET]: intl.formatMessage(BUTTON_MESSAGES.documentUploadBalanceSheet),
    };

    return documentLabelMap[documentType] ?? intl.formatMessage(BUTTON_MESSAGES.documentsButton);
  };

  const getButtonLabel = ({
    pageType,
    pageStep,
    isWithinSamePage,
    noTargetLabel,
  }: {
    pageType?: PAGE_TYPE;
    pageStep?: string;
    isWithinSamePage: boolean;
    noTargetLabel: string;
  }): string => {
    if (!pageType) {
      return noTargetLabel;
    }

    // When the navigation target is a step within the same page (e.g., next document in DOCUMENTS)
    if (isWithinSamePage) {
      if (pageType === PAGE_TYPE.DOCUMENTS) {
        return getDocumentLabel(pageStep);
      }
      return intl.formatMessage(BUTTON_MESSAGES.continueLabel);
    }

    switch (pageType) {
      case PAGE_TYPE.CONNECTORS:
        return intl.formatMessage(BUTTON_MESSAGES.platformsButton);
      case PAGE_TYPE.DOCUMENTS:
        return getDocumentLabel(pageStep);
      case PAGE_TYPE.CREDIT_CHECK_CONSENT:
        return intl.formatMessage(BUTTON_MESSAGES.softCreditCheckButton);
      case PAGE_TYPE.BANKING:
        return intl.formatMessage(BUTTON_MESSAGES.bankingButton);
      case PAGE_TYPE.BANK_STATEMENTS_UPLOAD:
        return intl.formatMessage(BUTTON_MESSAGES.bankStatementsButton);
      default:
        return intl.formatMessage(BUTTON_MESSAGES.continueLabel);
    }
  };

  const nextButtonLabel = getButtonLabel({
    pageType: nextStepWithinPage ? currentFlowPage.page : nextPageType,
    pageStep: nextStepWithinPage ?? nextPageSteps?.[0],
    isWithinSamePage: !!nextStepWithinPage,
    noTargetLabel: intl.formatMessage(BUTTON_MESSAGES.submitApplicationButton),
  });

  const backButtonLabel = getButtonLabel({
    pageType: previousPageContext.pageType,
    pageStep: previousPageContext.pageStep,
    isWithinSamePage: previousPageContext.pageType === currentFlowPage?.page,
    noTargetLabel: intl.formatMessage(COMMON_MESSAGES.backLabel),
  });

  const currentStepIndex = currentNavigationPageIndex - startPagesCount + 1;
  const stepString = intl.formatMessage(MESSAGES.stepperKickerLabel, {
    currentStep: currentStepIndex.toString(),
    totalSteps: navigationContext.totalSteps.toString(),
  });

  return {
    previousPath,
    nextPath,
    nextPageType,
    nextButtonLabel,
    backButtonLabel,
    navigateWithStateUpdate,
    navigate,
    currentStepIndex,
    stepString,
  };
};

export const LinearNavigationContext = createContext<NavigationContext | null>(null);

export const filterSelectedConnectors = (
  connectors: FundingApplicationSchemas.ConnectorPropertiesSchema[],
  connectorSlugsForJourney: string[]
) => connectors.filter((connector) => connectorSlugsForJourney.includes(connector.slug));

export const getRequiredDocsFromConfiguration = (
  fundingApplication: FormattedFundingApplicationScreen,
  fundingApplicationConfiguration: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
) => {
  const docTypes: FundingApplicationSchemas.DocumentType[] = [];
  if (fundingApplicationConfiguration.documents) {
    fundingApplicationConfiguration.documents.forEach((doc) => {
      docTypes.push(doc.documentType);
    });
  }

  return docTypes;
};

export const getPlatformsForApplication = (
  fundingApplication: FormattedFundingApplicationScreen,
  fundingApplicationConfiguration: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
) => {
  let requiredPlatforms: string[] = [];
  // Include any required connectors from configuration stages
  const connectorsForJourney = new Set([
    ...fundingApplication.selectedConnectorSlugs,
    ...(fundingApplicationConfiguration.revenue?.requiredConnectorSlugs ?? []),
    ...(fundingApplicationConfiguration.marketing?.requiredConnectorSlugs ?? []),
    ...(fundingApplicationConfiguration.accounting?.requiredConnectorSlugs ?? []),
  ]);
  if (connectorsForJourney) {
    requiredPlatforms = ORDERED_CONNECTOR_SLUGS.filter((slug) =>
      slug === "ga"
        ? connectorsForJourney.has(slug) && isGoogleAnalyticsRequired(Array.from(connectorsForJourney))
        : connectorsForJourney.has(slug)
    );
  }
  return requiredPlatforms;
};

export const calculateJourneyFromStages = (
  fundingApplication: FormattedFundingApplicationScreen,
  fundingApplicationConfiguration: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
): NavigationContext => {
  // Connections live in the side panel; banking is the first step when configured.
  const journey: ApplicationJourneyPage[] = fundingApplicationConfiguration.banking
    ? [{ page: PAGE_TYPE.BANKING }]
    : [];
  if (fundingApplicationConfiguration.includePlatformSelection) {
    journey.push({ page: PAGE_TYPE.CONNECTORS });
  }

  const requiredDocTypes = getRequiredDocsFromConfiguration(fundingApplication, fundingApplicationConfiguration);
  if (requiredDocTypes.length) {
    journey.push({ page: PAGE_TYPE.DOCUMENTS, steps: requiredDocTypes });
  }

  if (fundingApplicationConfiguration.creditConsent) {
    journey.push({ page: PAGE_TYPE.CREDIT_CHECK_CONSENT });
  }

  return { navigationFlow: journey, totalSteps: journey.length };
};

export interface NavigationContext {
  navigationFlow: ApplicationJourneyPage[];
  totalSteps: number;
}

export const useNavigationContext = (): NavigationContext => {
  const context = useContext(LinearNavigationContext);

  if (!context) {
    throw Error("LinearNavigationContext used outside LinearNavigationContextProvider");
  }
  return context as NavigationContext;
};
